import { useContext, useMemo } from 'react';

import { Button, Icons } from '@cast/design-system';

import { Link } from 'components/router';
import { useIsWoopDemoMode } from 'features/workload-optimization';

import { EventsRangePicker } from './_components/EventsRangePicker';
import { rangeCriterion } from './_constants';
import { ChartsContainer } from '../../_components/ChartsContainer';
import {
  EventsTable,
  MakeEventDetailsLink,
} from '../../_components/EventsTable';
import { useWorkloadAutoscalerEvents } from '../../_hooks/useWorkloadAutoscalerEvents';
import { WorkloadDetailsContext } from '../_context';
import { useWorkloadEventsTimeRangeSelection } from './_hooks/useWorkloadEventsTimeRangeSelection';
import { makeEventsLink } from '../../navigation';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { SearchProvider, Criterion } from '@cast/shared-search-ui';

// Always filter by workload ID, fixed criterion
const workloadIdCriterion = (workloadId: string): Criterion => ({
  key: 'workloadId',
  initialValue: workloadId,
});

const SeeWorkloadEventsButton = ({
  workloadId,
}: {
  workloadId: string | undefined;
}) => {
  const { currentPreset, timeZone, from, to } =
    useWorkloadEventsTimeRangeSelection();
  const isWoopDemoMode = useIsWoopDemoMode();
  const { cluster } = useCluster();

  return !isWoopDemoMode ? (
    <Button
      variant="secondary"
      size="sm"
      endIcon={<Icons.ArrowSquareOut />}
      component={Link}
      to={makeEventsLink({
        view: 'event-list',
        clusterId: cluster.id,
        searchValues: {
          workloadId,
          dateRange: {
            timezone: timeZone,
            preset: currentPreset,
            fromDate: currentPreset ? undefined : from,
            toDate: currentPreset ? undefined : to,
          },
        },
      })}
      testId="open-workload-events-button"
    >
      See workload events
    </Button>
  ) : undefined;
};

type WorkloadEventsSectionProps = {
  workloadId?: string;
  showSeeWorkloadEventsButton?: boolean;
  makeEventDetailsLink?: MakeEventDetailsLink;
  urlKey?: string;
  /** When true, search criteria are not read from or written to the URL. */
  disableUrlState?: boolean;
};

export const WorkloadEventsSection = ({
  workloadId: workloadIdProp,
  showSeeWorkloadEventsButton = true,
  makeEventDetailsLink,
  urlKey = 'workload-event-log',
  disableUrlState = false,
}: WorkloadEventsSectionProps) => {
  const { workload } = useContext(WorkloadDetailsContext);
  const workloadId = workloadIdProp ?? workload?.id;

  const criteria = useMemo(() => {
    return [workloadIdCriterion(workloadId || ''), rangeCriterion];
  }, [workloadId]);

  return (
    <SearchProvider
      useData={useWorkloadAutoscalerEvents}
      criteria={criteria}
      urlKey={disableUrlState ? undefined : urlKey}
    >
      <ChartsContainer
        testId="workload-events-section"
        title="Latest events"
        reverseActions
        noPaper
        dateRange={<EventsRangePicker />}
        selector={
          showSeeWorkloadEventsButton ? (
            <SeeWorkloadEventsButton workloadId={workloadId} />
          ) : undefined
        }
        charts={[
          {
            chart: (
              <EventsTable
                hideImpactedWorkloadsColumn
                maxHeight="60vh"
                makeEventDetailsLink={makeEventDetailsLink}
              />
            ),
          },
        ]}
      />
    </SearchProvider>
  );
};
